import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig, MatLegacyDialogModule as MatDialogModule, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { CustomerResolver } from '../../../dave-data-module/guards/customer.resolver';
import { getFetched$ } from '../../../dave-data-module/helper/helper';
import { MaterialToSupplierDataService } from '../../../dave-data-module/services/material-to-supplier-data.service';
import { State } from '../../../dave-data-module/State';
import {
    getCustomersFetched,
    getNotDeletedCustomers,
    getNotDeletedSuppliers,
} from '../../../dave-data-module/State/selectors/customers.selectors';
import { getMaterialGroups } from '../../../dave-data-module/State/selectors/material-group.selectors';
import { AppButtonModule } from '../../../dave-utils-module/app-button-module/app-button.module';
import { SelectSearchOption } from '../../../dave-utils-module/select-search/components/select-search/select-search.component';
import { SelectSearchModule } from '../../../dave-utils-module/select-search/select-search.module';
import { appMatDialogDefaultConfig, isNotNullOrUndefined } from '../../../helper/helper';
import { HintIconComponent } from '../../../hint-icon/hint-icon.component';
import { LoadingService } from '../../../services/loading.service';
import {
    MaterialPriceHistoryDataService
} from '../../../dave-data-module/services/material-price-history-data.service';
import { financialRound } from '../../../dave-data-module/entities/accounts-receivable-ledger.entity';
import { CustomLabelService } from '../../../services/custom-label/custom-label.service';

export interface EditMaterialToSupplierDialogComponentDialogData {
    name?: string;
    description?: string;
    customer?: SelectSearchOption<{     Id: number;   }>;
    articlenumber?: string;
    /**
     * in cents
     */
    cost?: number;
    sellPrice?: number;
    listPrice?: number;

    productGroup?: SelectSearchOption<{     Id: number;   }>;
    materialId: number;
}

export type EditMaterialToSupplierComponentDialogReturnData = undefined;

function requireOneControl(requiredControlNames: string[]) {
    return (formGroup: FormGroup) => {
        if (requiredControlNames.some((key) => formGroup.get(key).value)) {
            return null;
        }
        return { required: 'at least one of the items is required' };
    };
}

@Component({
    selector: 'app-edit-material-to-supplier-dialog',
    standalone: true,
    imports: [CommonModule, MatDialogModule, MatInputModule, SelectSearchModule, ReactiveFormsModule, MatCheckboxModule, AppButtonModule, MatMenuModule, FontAwesomeModule, MatTooltipModule, HintIconComponent],
    templateUrl: './edit-material-to-supplier-dialog.component.html',
    styleUrls: ['./edit-material-to-supplier-dialog.component.scss'],
})
export class EditMaterialToSupplierDialogComponent implements OnInit {
    public static readonly DefaultConfig: MatDialogConfig = {
        ...appMatDialogDefaultConfig,
    };
    protected form = new FormGroup({
        name: new FormControl<string>(''),
        description: new FormControl<string>(''),
        customer: new FormControl<SelectSearchOption<{ Id: number }>>(null, [Validators.required]),
        cost: new FormControl<number>(0),
        sellPrice: new FormControl<number>(0),
        listPrice: new FormControl<number>(0),
        articlenumber: new FormControl<string>(''),
        mainProductGroup: new FormControl(null, [Validators.required]),
        productGroup: new FormControl(null, [Validators.required]),
        // materialId: new FormControl<number>(null),
    });
    protected customers$ = getFetched$(this.store, getCustomersFetched, getNotDeletedSuppliers, this.customerRes).pipe(
        map((customers) =>
            customers
                .filter((c) => true)
                .map<SelectSearchOption<{ Id: number }>>((c) => ({ Id: c.Id, optionLabel: c.DisplayName }))
        ),
    );

    protected mainProductGroups$ = this.store.select(getMaterialGroups).pipe(map((groups) => groups.filter((group) => !group.ParentId).map((group) => ({ Id: group.Id, optionLabel: group.Name }))));

    protected productGroups$ = this.form.controls.mainProductGroup.valueChanges.pipe(
        startWith(this.form.controls.mainProductGroup.value),
        switchMap((mainProductGroup) => {
            return this.store.select(getMaterialGroups).pipe(
                map((groups) => {
                    if (!mainProductGroup) {
                        return groups.filter((group) => group.ParentId).map((group) => ({ Id: group.Id, optionLabel: group.Name }));
                    }
                    return groups.filter((group) => group.ParentId === mainProductGroup.Id).map((group) => ({ Id: group.Id, optionLabel: group.Name }));
                }),
            );
        }),
    );
    protected supplierLabel$: Observable<string> = this.cls.getSingle$('Supplier');

    constructor(
        public LS: LoadingService,
        private store: Store<State>,
        private dialog: MatDialog,
        private customerRes: CustomerResolver,
        protected ls: LoadingService,
        protected cls: CustomLabelService,
        @Inject(MAT_DIALOG_DATA) public data: EditMaterialToSupplierDialogComponentDialogData,
        private materialToSupplierDataService: MaterialToSupplierDataService,
        private materialPriceHistoryDataService: MaterialPriceHistoryDataService,
    ) {
        if (this.data) {
            this.form.patchValue({
                name: this.data.name || '',
                description: this.data.description || '',
                customer: this.data.customer,
                cost: financialRound((this.data.cost || 0) / 100),
                sellPrice: financialRound((this.data.sellPrice || 0) / 100),
                listPrice: financialRound((this.data.listPrice || 0) / 100),
                articlenumber: this.data.articlenumber || '',
                mainProductGroup: null,
                productGroup: this.data.productGroup || null,
                // materialId: this.data.MaterialId || null,
            });

            if (this.data.productGroup) {
                this.store
                    .select(getMaterialGroups)
                    .pipe(
                        map((groups) => {
                            const subGroup = groups.find((group) => group.Id === data.productGroup.Id);
                            const mainGroup = groups.find((group) => group.Id === subGroup?.ParentId);
                            return mainGroup;
                        }),
                    )
                    .subscribe((mainGroup) => {
                        if (mainGroup) {
                            this.form.controls.mainProductGroup.setValue(mainGroup);
                        }
                    });
            }
        }

        this.form.controls.productGroup.valueChanges.subscribe((productGroup) => {
            if (productGroup) {
                this.store
                    .select(getMaterialGroups)
                    .pipe(
                        map((groups) => {
                            const subGroup = groups.find((group) => group.Id === productGroup.Id);
                            const mainGroup = groups.find((group) => group.Id === subGroup?.ParentId);
                            return mainGroup;
                        }),
                    )
                    .subscribe((mainGroup) => {
                        if (mainGroup) {
                            this.form.controls.mainProductGroup.setValue(mainGroup);
                        }
                    });
            }
        });
    }
    ngOnInit() {
        if (this.data.customer) {
            this.form.controls.customer.disable();
        } else {
            this.form.controls.customer.enable();
        }
    }

    protected compareById(a: { Id: number }, b: { Id: number }) {
        return a.Id === b.Id;
    }

    onSubmitClick() {
        if (this.data.customer != null) {
            this.materialToSupplierDataService.change({
                Name: String(this.form.value.name),
                Description: this.form.value.description,
                MaterialId: String(this.data.materialId),
                SupplierId: String(this.data.customer.Id),
                Cost: String(financialRound(this.form.value.cost * 100, 0)),
                ListPrice: String(financialRound(this.form.value.listPrice * 100, 0)),
                SellPrice: String(financialRound(this.form.value.sellPrice * 100, 0)),
                ArticleNumber: String(this.form.value.articlenumber),
                MaterialGroupId: String(this.form.value.productGroup.Id),
            }).then(() => {
                this.materialPriceHistoryDataService.getMaterialPriceHistoryByMaterialId$(this.data.materialId, true)
            })
        } else {
            this.materialToSupplierDataService.add({
                Name: this.form.value.name ? String(this.form.value.name) : '',
                Description: this.form.value.description ? String(this.form.value.description) : '',
                MaterialId: this.data.materialId && String(this.data.materialId),
                SupplierId: this.form.value.customer?.Id && String(this.form.value.customer.Id),
                Cost: isNotNullOrUndefined(this.form.value.cost) && String(financialRound(this.form.value.cost * 100, 0)),
                ListPrice: String(financialRound(this.form.value.listPrice * 100, 0)),
                SellPrice: String(financialRound(this.form.value.sellPrice * 100, 0)),
                ArticleNumber: this.form.value.articlenumber ? String(this.form.value.articlenumber) : '',
                MaterialGroupId: this.form.value.productGroup.Id && String(this.form.value.productGroup.Id),
            }).then(() => {
                this.materialPriceHistoryDataService.getMaterialPriceHistoryByMaterialId$(this.data.materialId, true)
            })
        }
        this.dialog.closeAll();
    }

    onDelete() {
        this.materialToSupplierDataService.delete({
            MaterialId: String(this.data.materialId),
            SupplierId: String(this.data.customer.Id),
        });
        this.dialog.closeAll();
    }
}
